import { useEffect, useState, useCallback, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../lib/formatting'
import {
  Plus,
  Search,
  Loader2,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Download,
  Upload,
  User as UserIcon,
  Building2,
  Send,
  Mail,
  Phone,
  Star,
} from 'lucide-react'
import SortableTh, { type SortState } from '../components/ui/SortableTh'
import { api, ApiError } from '../lib/api'
import { confirmDialog, toast } from '../lib/dialogs'
import { downloadFile } from '../lib/download'
import { formatCents } from '../lib/money'
import Modal from '../components/ui/Modal'
import ClientRatesPanel from '../components/rates/ClientRatesPanel'
import EmailComposer from '../components/comms/EmailComposer'
import SentEmailsPanel from '../components/comms/SentEmailsPanel'
import ImportClientsModal from '../components/clients/ImportClientsModal'
import { useAuth } from '../contexts/AuthContext'
import type {
  ClientDetail,
  ClientListItem,
  ClientListResponse,
  ClientProfitability,
  ClientSortBy,
  ClientType,
  CreateClientInput,
  ClientTagDto,
  ClientTagListResponse,
} from '@toga/shared'

const PAGE_SIZE = 25

export default function Clients() {
  const { t } = useTranslation(['clients', 'common'])
  const auth = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const canWrite = auth.status === 'authenticated' && auth.user.role !== 'LECTURA'
  // Importar crea clientes EN MASA: mismo criterio que el backend, que lo
  // restringe a ADMIN y CONSULTOR. Enseñar el botón a quien va a recibir un 403
  // es peor que no enseñarlo.
  const canImport =
    auth.status === 'authenticated' &&
    (auth.user.role === 'ADMIN' || auth.user.role === 'CONSULTOR')

  const [data, setData] = useState<ClientListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [q, setQ] = useState('')
  const [typeFilter, setTypeFilter] = useState<ClientType | ''>('')
  // Filtro por etiqueta: es lo que convierte el listado en el sitio donde se
  // arma una campaña («enséñame a los de la feria de Madrid»).
  const [tagFilter, setTagFilter] = useState<string>('')
  const [tagCatalog, setTagCatalog] = useState<ClientTagDto[]>([])
  const [importing, setImporting] = useState(false)
  const [page, setPage] = useState(1)
  // Orden elegido en cabecera; null = orden por defecto del backend
  // (relevancia si hay búsqueda, alta DESC si no).
  const [sort, setSort] = useState<SortState<ClientSortBy> | null>(null)

  const [editing, setEditing] = useState<ClientDetail | 'new' | null>(null)

  // F39: filas expandidas para mostrar contactos. Se autoexpanden las que
  // tienen matchedContactIds (la búsqueda matcheó por contacto).
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (typeFilter) params.set('type', typeFilter)
      if (tagFilter) params.set('tagIds', tagFilter)
      if (sort) {
        params.set('sortBy', sort.by)
        params.set('sortDir', sort.dir)
      }
      params.set('page', String(page))
      params.set('pageSize', String(PAGE_SIZE))
      const resp = await api.get<ClientListResponse>(`/clients?${params.toString()}`)
      setData(resp)
      // Autoexpandir los clientes cuyo match vino por contacto: así el
      // usuario ve directamente la persona que se buscaba.
      setExpanded((prev) => {
        const next = new Set(prev)
        for (const c of resp.items) {
          if (c.matchedContactIds && c.matchedContactIds.length > 0) next.add(c.id)
        }
        return next
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.load'))
    } finally {
      setLoading(false)
    }
  }, [q, typeFilter, tagFilter, page, sort])

  useEffect(() => {
    void reload()
  }, [reload])

  // El catálogo se carga una vez: el selector solo aparece si hay etiquetas, así
  // que quien no las use no ve un desplegable vacío.
  useEffect(() => {
    void api
      .get<ClientTagListResponse>('/client-tags')
      .then((r) => setTagCatalog(r.items))
      .catch(() => setTagCatalog([]))
  }, [])

  // Volver a página 1 cuando cambian los filtros o el orden
  useEffect(() => {
    setPage(1)
  }, [q, typeFilter, tagFilter, sort])

  const toggleSort = useCallback((by: ClientSortBy) => {
    setSort((prev) => {
      if (prev?.by === by) return { by, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      // Alta: lo natural es empezar por lo más reciente; el resto, alfabético.
      return { by, dir: by === 'createdAt' ? 'desc' : 'asc' }
    })
  }, [])

  const onEdit = useCallback(async (id: string) => {
    try {
      const { client } = await api.get<{ client: ClientDetail }>(`/clients/${id}`)
      setEditing(client)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common:errors.generic'))
    }
  }, [t])

  // Soporte para abrir el modal de edición desde ClientDetailPage vía ?edit=<id>.
  // Al cargarlo, limpiamos el query string para no reabrir en sucesivos renders.
  useEffect(() => {
    const editId = searchParams.get('edit')
    if (editId) {
      void onEdit(editId)
      const next = new URLSearchParams(searchParams)
      next.delete('edit')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams, onEdit])

  const onDelete = async (c: ClientListItem) => {
    if (!(await confirmDialog({ message: t('confirmDelete', { name: c.displayName }), tone: 'danger' })))
      return
    try {
      await api.delete(`/clients/${c.id}`)
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common:errors.generic'))
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1

  const [exporting, setExporting] = useState(false)
  const onExport = async () => {
    setExporting(true)
    setError(null)
    try {
      await downloadFile('/export/clients.csv', 'clientes.csv')
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.export'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">
            {/* La clave i18n ya interpola el número ("{{count}} Clientes"):
                no anteponer data.total o sale duplicado ("9 9 Clientes"). */}
            {t('title', { count: data?.total ?? 0 })}
          </h2>
          <p className="t-muted text-sm mt-1">{t('subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void onExport()}
            disabled={exporting}
            title={t('exportCsvTitle')}
            className="px-3 py-2 rounded-lg text-xs border border-base t-secondary hover:bg-hover hover:text-toga-700 dark:hover:text-toga-200 flex items-center gap-1.5 disabled:opacity-50"
          >
            {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            {t('exportCsv')}
          </button>
          {canImport && (
            <button
              type="button"
              onClick={() => setImporting(true)}
              title={t('import.subtitle')}
              className="px-3 py-2 rounded-lg text-xs border border-base t-secondary hover:bg-hover hover:text-toga-700 dark:hover:text-toga-200 flex items-center gap-1.5"
            >
              <Upload size={12} /> {t('import.title')}
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
            >
              <Plus size={16} /> {t('newButton')}
            </button>
          )}
        </div>
      </header>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 t-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 rounded-full bg-[var(--bg-input)] border border-[var(--border)] t-primary placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as ClientType | '')}
          className="px-3 py-2 rounded-full bg-[var(--bg-input)] border border-[var(--border)] t-primary"
        >
          <option value="">{t('filter.allTypes')}</option>
          <option value="PERSONA">{t('filter.persons')}</option>
          <option value="EMPRESA">{t('filter.companies')}</option>
        </select>
        {tagCatalog.length > 0 && (
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            className="px-3 py-2 rounded-full bg-[var(--bg-input)] border border-[var(--border)] t-primary"
            aria-label={t('tags.filter')}
          >
            <option value="">{t('tags.filterAll')}</option>
            {tagCatalog.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
                {tag.clientCount === undefined ? '' : ` (${tag.clientCount})`}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider t-muted border-b border-base">
              <th className="w-8 px-2 py-3"></th>
              <SortableTh label={t('table.client')} field="name" sort={sort} onSort={toggleSort} />
              <SortableTh label={t('table.nationalId')} field="nationalId" sort={sort} onSort={toggleSort} />
              <th className="px-4 py-3 font-medium">{t('table.contact')}</th>
              <SortableTh label={t('table.city')} field="city" sort={sort} onSort={toggleSort} />
              <SortableTh label={t('table.createdAt')} field="createdAt" sort={sort} onSort={toggleSort} />
              <th className="px-4 py-3 font-medium text-right">{t('table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center t-muted">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> {t('common:loading')}
                </td>
              </tr>
            ) : data && data.items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center t-muted">
                  {q || typeFilter ? t('empty.noResults') : t('empty.noClients')}
                </td>
              </tr>
            ) : (
              data?.items.flatMap((c) => {
                const isExpanded = expanded.has(c.id)
                const hasContacts = c.contacts && c.contacts.length > 0
                const matched = new Set(c.matchedContactIds ?? [])
                const mainRow = (
                  <tr
                    key={c.id}
                    className="border-b border-base/50 hover:bg-hover/50 cursor-pointer"
                    onClick={() => navigate(`/clients/${c.id}`)}
                    title={t('row.openTitle')}
                  >
                    <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                      {hasContacts ? (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(c.id)}
                          className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted"
                          aria-label={isExpanded ? t('row.collapseContacts') : t('row.expandContacts')}
                          title={t('row.contactsCount', { count: c.contacts.length })}
                        >
                          <ChevronDown
                            size={14}
                            className={`transition-transform ${isExpanded ? '' : '-rotate-90'}`}
                          />
                        </button>
                      ) : (
                        <span className="w-7 h-7 inline-block" />
                      )}
                    </td>
                    <td className="px-4 py-3 t-primary font-medium">
                      <div className="flex items-center gap-2.5">
                        <span
                          className={[
                            'w-8 h-8 rounded-lg grid place-items-center',
                            c.type === 'EMPRESA'
                              ? 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300'
                              : 'bg-purple-500/15 text-purple-700 dark:text-purple-300',
                          ].join(' ')}
                        >
                          {c.type === 'EMPRESA' ? <Building2 size={14} /> : <UserIcon size={14} />}
                        </span>
                        <span>{c.displayName}</span>
                        {hasContacts && (
                          <span className="text-[10px] t-muted font-normal">
                            · {t('row.personsCount', { count: c.contacts.length })}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 t-secondary font-mono text-xs">{c.nationalId ?? '—'}</td>
                    <td className="px-4 py-3 t-secondary text-xs">
                      <div>{c.email ?? '—'}</div>
                      <div className="t-faint">{c.phone ?? ''}</div>
                    </td>
                    <td className="px-4 py-3 t-secondary text-xs">{c.city ?? '—'}</td>
                    <td className="px-4 py-3 t-muted text-xs">
                      {new Date(c.createdAt).toLocaleDateString(currentLocaleTag())}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => onEdit(c.id)}
                          title={t('row.editTitle')}
                          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-700 dark:hover:text-toga-200"
                        >
                          <Pencil size={14} />
                        </button>
                        {canWrite && (
                          <button
                            type="button"
                            onClick={() => onDelete(c)}
                            title={t('common:actions.delete')}
                            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )

                if (!isExpanded || !hasContacts) return [mainRow]

                // Fila de sub-cabecera + una fila por contacto, todas usando las
                // MISMAS columnas de la tabla padre (chevron / cliente / NIF /
                // contacto / ciudad / alta / acciones) para que las columnas
                // queden alineadas visualmente con la fila del cliente.
                const subHeader = (
                  <tr
                    key={`${c.id}-contacts-header`}
                    className="bg-[var(--bg-input-soft)]/60 text-[10px] uppercase tracking-wider t-muted"
                  >
                    <td className="px-2 py-1"></td>
                    <td className="px-4 py-1 font-medium">{t('contacts.person')}</td>
                    <td className="px-4 py-1 font-medium">{t('contacts.role')}</td>
                    <td className="px-4 py-1 font-medium">{t('contacts.email')}</td>
                    <td className="px-4 py-1 font-medium">{t('contacts.phone')}</td>
                    <td className="px-4 py-1"></td>
                    <td className="px-4 py-1"></td>
                  </tr>
                )

                const contactRows = c.contacts.map((p) => {
                  const isMatch = matched.has(p.id)
                  return (
                    <tr
                      key={`${c.id}-contact-${p.id}`}
                      className={[
                        'border-b border-base/30 bg-[var(--bg-input-soft)]',
                        isMatch ? 'bg-amber-500/10' : '',
                      ].join(' ')}
                      title={isMatch ? t('contacts.matchTitle') : ''}
                    >
                      <td className="px-2 py-1.5"></td>
                      <td className="px-4 py-1.5 t-primary text-xs">
                        <span className="inline-flex items-center gap-1.5">
                          {p.isPrimary && (
                            <Star
                              size={11}
                              className="text-amber-700 dark:text-amber-300 fill-amber-500/40"
                            />
                          )}
                          {p.name}
                        </span>
                      </td>
                      <td className="px-4 py-1.5 t-secondary text-xs">{p.role ?? '—'}</td>
                      <td className="px-4 py-1.5 t-secondary text-xs">
                        {p.email ? (
                          <a
                            href={`mailto:${p.email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 hover:text-toga-500"
                          >
                            <Mail size={11} /> {p.email}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-1.5 t-secondary text-xs">
                        {p.phone ? (
                          <a
                            href={`tel:${p.phone}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 hover:text-toga-500"
                          >
                            <Phone size={11} /> {p.phone}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-1.5"></td>
                      <td className="px-4 py-1.5"></td>
                    </tr>
                  )
                })

                return [mainRow, subHeader, ...contactRows]
              })
            )}
          </tbody>
        </table>

        {/* Paginación */}
        {data && data.total > data.pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-base text-sm">
            <span className="t-muted">
              {t('pagination', { page: data.page, totalPages, total: data.total })}
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={data.page <= 1}
                className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted disabled:opacity-30"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={data.page >= totalPages}
                className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted disabled:opacity-30"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {importing && (
        <ImportClientsModal
          onClose={() => setImporting(false)}
          onImported={() => {
            void reload()
            // El catálogo puede haber crecido: el importador crea las etiquetas
            // que venían en el fichero y no existían.
            void api
              .get<ClientTagListResponse>('/client-tags')
              .then((r) => setTagCatalog(r.items))
              .catch(() => undefined)
          }}
        />
      )}

      {editing !== null && (
        <ClientFormModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void reload()
          }}
        />
      )}
    </div>
  )
}

interface ClientFormProps {
  initial: ClientDetail | null
  onClose: () => void
  onSaved: () => void
}

function emptyForm(): CreateClientInput {
  return {
    type: 'PERSONA',
    firstName: '',
    lastName1: '',
    lastName2: '',
    legalName: '',
    nationalId: '',
    esNacional: true,
    currency: 'EUR',
    language: 'es',
    vatNumber: '',
    email: '',
    phone: '',
    mobile: '',
    web: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
    country: 'ES',
    notes: '',
  }
}

function ClientFormModal({ initial, onClose, onSaved }: ClientFormProps) {
  const { t } = useTranslation(['clients', 'common'])
  const auth = useAuth()
  const canWrite =
    auth.status === 'authenticated' && auth.user.role !== 'LECTURA'
  const isEdit = initial !== null
  const [form, setForm] = useState<CreateClientInput>(
    initial
      ? {
          type: initial.type,
          firstName: initial.firstName ?? '',
          lastName1: initial.lastName1 ?? '',
          lastName2: initial.lastName2 ?? '',
          legalName: initial.legalName ?? '',
          nationalId: initial.nationalId ?? '',
          esNacional: initial.esNacional,
          currency: initial.currency,
          language: initial.language,
          vatNumber: initial.vatNumber ?? '',
          email: initial.email ?? '',
          phone: initial.phone ?? '',
          mobile: initial.mobile ?? '',
          web: initial.web ?? '',
          address: initial.address ?? '',
          city: initial.city ?? '',
          postalCode: initial.postalCode ?? '',
          province: initial.province ?? '',
          country: initial.country,
          notes: initial.notes ?? '',
        }
      : emptyForm()
  )
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // F29 — Email composer + refresh trigger del historial al enviar.
  const [composerOpen, setComposerOpen] = useState(false)
  const [sentEmailsRefreshKey, setSentEmailsRefreshKey] = useState(0)

  const upd = <K extends keyof CreateClientInput>(k: K, v: CreateClientInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      // Convertimos strings vacíos a null para que pasen las validaciones .nullable()
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
      ) as unknown as CreateClientInput
      payload.type = form.type
      payload.country = form.country || 'ES'
      payload.esNacional = form.esNacional
      payload.currency = (form.currency || 'EUR').toUpperCase()
      payload.language = (form.language || 'es').toLowerCase()

      if (isEdit && initial) {
        await api.patch(`/clients/${initial.id}`, payload)
      } else {
        await api.post('/clients', payload)
      }
      onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as
          | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
          | undefined
        const fieldErr = body?.issues?.fieldErrors
        const firstFieldErr = fieldErr ? Object.values(fieldErr).flat().find(Boolean) : undefined
        setError(firstFieldErr ?? body?.error ?? t('form.errors.saveFailed'))
      } else {
        setError(t('form.errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={isEdit ? t('form.titleEdit') : t('form.titleNew')}
      maxWidth="max-w-2xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => upd('type', 'PERSONA')}
            className={[
              'flex-1 px-3 py-2 rounded-lg text-sm border flex items-center justify-center gap-2 transition',
              form.type === 'PERSONA'
                ? 'bg-purple-500/15 border-purple-500/40 text-purple-700 dark:text-purple-200'
                : 'bg-[var(--bg-input-soft)] border-[var(--border)] t-secondary hover:bg-[var(--bg-input)]',
            ].join(' ')}
            disabled={isEdit}
          >
            <UserIcon size={14} /> {t('form.personType')}
          </button>
          <button
            type="button"
            onClick={() => upd('type', 'EMPRESA')}
            className={[
              'flex-1 px-3 py-2 rounded-lg text-sm border flex items-center justify-center gap-2 transition',
              form.type === 'EMPRESA'
                ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-700 dark:text-indigo-200'
                : 'bg-[var(--bg-input-soft)] border-[var(--border)] t-secondary hover:bg-[var(--bg-input)]',
            ].join(' ')}
            disabled={isEdit}
          >
            <Building2 size={14} /> {t('form.companyType')}
          </button>
        </div>

        {/* Nacional / Internacional — condiciona la validación del NIF y los datos fiscales */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => upd('esNacional', true)}
            className={[
              'flex-1 px-3 py-2 rounded-lg text-sm border transition',
              form.esNacional
                ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-200'
                : 'bg-[var(--bg-input-soft)] border-[var(--border)] t-secondary hover:bg-[var(--bg-input)]',
            ].join(' ')}
          >
            {t('form.national')}
          </button>
          <button
            type="button"
            onClick={() => upd('esNacional', false)}
            className={[
              'flex-1 px-3 py-2 rounded-lg text-sm border transition',
              !form.esNacional
                ? 'bg-amber-500/15 border-amber-500/40 text-amber-700 dark:text-amber-200'
                : 'bg-[var(--bg-input-soft)] border-[var(--border)] t-secondary hover:bg-[var(--bg-input)]',
            ].join(' ')}
          >
            {t('form.international')}
          </button>
        </div>

        {form.type === 'PERSONA' ? (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs t-muted mb-1.5">{t('form.firstName')}</label>
              <input
                required
                value={form.firstName ?? ''}
                onChange={(e) => upd('firstName', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs t-muted mb-1.5">{t('form.lastName1')}</label>
              <input
                required
                value={form.lastName1 ?? ''}
                onChange={(e) => upd('lastName1', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs t-muted mb-1.5">{t('form.lastName2')}</label>
              <input
                value={form.lastName2 ?? ''}
                onChange={(e) => upd('lastName2', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.legalName')}</label>
            <input
              required
              value={form.legalName ?? ''}
              onChange={(e) => upd('legalName', e.target.value)}
              className={inputCls}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">
              {form.esNacional ? t('form.nationalIdEs') : t('form.nationalIdIntl')}
            </label>
            <input
              value={form.nationalId ?? ''}
              onChange={(e) =>
                upd(
                  'nationalId',
                  // Solo forzamos mayúsculas para clientes nacionales (formato español);
                  // los internacionales se dejan tal cual (validación libre).
                  form.esNacional ? e.target.value.toUpperCase() : e.target.value
                )
              }
              placeholder={form.esNacional ? '12345678Z' : t('form.optional')}
              className={`${inputCls} font-mono`}
            />
            <p className="text-[11px] t-faint mt-1">
              {form.esNacional
                ? t('form.nationalIdHintEs')
                : t('form.nationalIdHintIntl')}
            </p>
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">
              {form.esNacional ? t('form.vatNumber') : t('form.vatNumberIntra')}
            </label>
            <input
              value={form.vatNumber ?? ''}
              onChange={(e) => upd('vatNumber', e.target.value.toUpperCase())}
              placeholder={form.esNacional ? 'ESX1234567X' : t('form.vatPlaceholderIntl')}
              className={`${inputCls} font-mono`}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.currency')}</label>
            <input
              value={form.currency ?? ''}
              onChange={(e) => upd('currency', e.target.value.toUpperCase())}
              maxLength={3}
              placeholder="EUR"
              list="client-currency-options"
              className={`${inputCls} font-mono uppercase`}
            />
            <datalist id="client-currency-options">
              <option value="EUR" />
              <option value="USD" />
              <option value="GBP" />
              <option value="CHF" />
            </datalist>
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.language')}</label>
            <input
              value={form.language ?? ''}
              onChange={(e) => upd('language', e.target.value.toLowerCase())}
              maxLength={2}
              placeholder="es"
              list="client-language-options"
              className={`${inputCls} font-mono lowercase`}
            />
            <datalist id="client-language-options">
              <option value="es" />
              <option value="en" />
              <option value="fr" />
              <option value="de" />
              <option value="pt" />
            </datalist>
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.email')}</label>
            <input
              type="email"
              value={form.email ?? ''}
              onChange={(e) => upd('email', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.phone')}</label>
            <input
              value={form.phone ?? ''}
              onChange={(e) => upd('phone', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.mobile')}</label>
            <input
              value={form.mobile ?? ''}
              onChange={(e) => upd('mobile', e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">{t('form.web')}</label>
          <input
            type="text"
            inputMode="url"
            placeholder={t('form.webPlaceholder')}
            value={form.web ?? ''}
            onChange={(e) => upd('web', e.target.value)}
            className={inputCls}
          />
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">{t('form.address')}</label>
          <input
            value={form.address ?? ''}
            onChange={(e) => upd('address', e.target.value)}
            className={inputCls}
          />
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="col-span-2">
            <label className="block text-xs t-muted mb-1.5">{t('form.city')}</label>
            <input
              value={form.city ?? ''}
              onChange={(e) => upd('city', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.postalCode')}</label>
            <input
              value={form.postalCode ?? ''}
              onChange={(e) => upd('postalCode', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.province')}</label>
            <input
              value={form.province ?? ''}
              onChange={(e) => upd('province', e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">{t('form.notes')}</label>
          <textarea
            value={form.notes ?? ''}
            onChange={(e) => upd('notes', e.target.value)}
            rows={3}
            className={inputCls}
          />
        </div>

        {isEdit && initial && (
          <ClientRatesPanel clientId={initial.id} canWrite={canWrite} />
        )}

        {isEdit && initial && canWrite && (
          <div className="flex items-center justify-between gap-3 border border-[var(--border)]/70 rounded-lg px-3 py-2 bg-[var(--bg-input-soft)]">
            <div className="min-w-0">
              <p className="t-primary text-sm font-medium">{t('form.emailClient')}</p>
              <p className="t-faint text-[11px] truncate">
                {form.email || initial.email
                  ? t('form.recipient', { email: form.email || initial.email })
                  : t('form.recipientHint')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setComposerOpen(true)}
              className="shrink-0 px-3 py-2 rounded-lg text-sm bg-toga-500/10 text-toga-700 dark:text-toga-200 hover:bg-toga-500/15 flex items-center gap-1.5"
            >
              <Send size={14} /> {t('form.sendEmail')}
            </button>
          </div>
        )}

        {isEdit && initial && (
          <SentEmailsPanel clientId={initial.id} refreshKey={sentEmailsRefreshKey} />
        )}

        {isEdit && initial && <ClientProfitabilityCard p={initial.profitability} />}

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? t('form.saveChanges') : t('form.createClient')}
          </button>
        </div>
      </form>

      {isEdit && initial && (
        <EmailComposer
          open={composerOpen}
          onClose={() => setComposerOpen(false)}
          defaultTo={form.email || initial.email || undefined}
          clientId={initial.id}
          onSent={() => setSentEmailsRefreshKey((k) => k + 1)}
        />
      )}
    </Modal>
  )
}

/** Formatea una fracción (0.42) como porcentaje es-ES: "42,0 %". Admite negativos. */
function formatPct(fraction: number): string {
  return `${(fraction * 100).toLocaleString(currentLocaleTag(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`
}

/**
 * Tarjeta INTERNA de rentabilidad del cliente: CAC, LTV y CLTV.
 * Estilo ámbar como las cifras internas — NUNCA visible para el cliente.
 */
function ClientProfitabilityCard({ p }: { p: ClientProfitability }) {
  const { t } = useTranslation('clients')
  const marginPositive = p.grossMarginPct >= 0
  const cltvPositive = p.cltvCents >= 0

  return (
    <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-4 text-xs space-y-2">
      <div className="flex items-center justify-between text-amber-700 dark:text-amber-300/80">
        <span className="uppercase tracking-wider text-[10px] font-semibold">
          {t('profitability.title')}
        </span>
        <span className="text-[9px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-700 dark:text-amber-300/70">
          {t('profitability.internalBadge')}
        </span>
      </div>

      <div className="flex justify-between text-amber-700 dark:text-amber-200/80">
        <span>{t('profitability.wonProjects')}</span>
        <span className="font-mono">{p.wonProjectsCount}</span>
      </div>

      <div className="flex justify-between text-amber-700 dark:text-amber-200/80">
        <span>
          CAC<span className="text-amber-700 dark:text-amber-300/50 ml-1">{t('profitability.cacHint')}</span>
        </span>
        <span className="font-mono">{formatCents(p.cacCents)}</span>
      </div>

      <div className="flex justify-between text-amber-700 dark:text-amber-200/80">
        <span>
          LTV<span className="text-amber-700 dark:text-amber-300/50 ml-1">{t('profitability.ltvHint')}</span>
        </span>
        <span className="font-mono">{formatCents(p.ltvCents)}</span>
      </div>

      <div className="flex justify-between text-amber-800 dark:text-amber-100/90 font-semibold border-t border-amber-500/20 pt-2 mt-1">
        <span>
          CLTV<span className="text-amber-700 dark:text-amber-300/50 ml-1 font-normal">{t('profitability.cltvHint')}</span>
        </span>
        <span className={`font-mono ${cltvPositive ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
          {formatCents(p.cltvCents)}
        </span>
      </div>

      <div className="flex items-center justify-between text-amber-800 dark:text-amber-100/90 font-semibold">
        <span>{t('profitability.margin')}</span>
        <span
          className={`font-mono text-[11px] px-1.5 py-0.5 rounded ${
            marginPositive ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-rose-500/10 text-rose-700 dark:text-rose-300'
          }`}
        >
          {formatPct(p.grossMarginPct)}
        </span>
      </div>
    </div>
  )
}
